import { Link } from "react-router-dom";
import { expandOccurrences } from "@/lib/occurrences";
import { useState, useMemo } from "react";
import Seo from "@/components/Seo";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight, ArrowUpRight, Star, MapPin, Compass, Users, Sparkle, HandHeart,
  Wind, Moon, Sun, HeartHandshake, Globe2, ChevronLeft, ChevronRight, CalendarDays, Clock,
  BatteryLow, CloudRain, Briefcase, Sprout, Flower2, Sparkles, Building2, Tent,
} from "lucide-react";
import api from "@/lib/api";
import EventModal from "@/components/EventModal";
import CorporateInquiryModal from "@/components/CorporateInquiryModal";

const HERO_IMG = "https://images.unsplash.com/photo-1758225104742-718edea1f371?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxmcmllbmRzJTIwbGF1Z2hpbmclMjBpbiUyMGZvcmVzdCUyMHdhcm0lMjBzdW5saWdodHxlbnwwfHx8fDE3ODg5NTE2NTl8MA&ixlib=rb-4.1.0&q=85";
const CONNECT_IMG = "https://images.unsplash.com/photo-1601749990579-c03146da4d0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY6NzF8MHwxfHNlYXJjaHw0fHxwZW9wbGUlMjBuYXR1cmUlMjBvdXRkb29yJTIwZXhwbG9yYXRpb24lMjBnYXRoZXJpbmclMjBjb21tdW5pdHl8ZW58MHx8fHwxNzg4OTUxNjUyfDA&ixlib=rb-4.1.0&q=85";
const WORLD_IMG = "https://images.unsplash.com/photo-1783774044351-29334596b26a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxmcmllbmRzJTIwbGF1Z2hpbmclMjBpbiUyMGZvcmVzdCUyMHdhcm0lMjBzdW5saWdodHxlbnwwfHx8fDE3ODg5NTE2NTl8MA&ixlib=rb-4.1.0&q=85";

const NEED_CARDS = [
  { id: "rest", icon: Moon, title: "Rest & Restoration", text: "Ease nervous tension, replenish energy, and return to deep rest." },
  { id: "emotional", icon: HandHeart, title: "Emotional Clarity & Balance", text: "Nurture inner calm, navigate complex emotions, and grow resilience." },
  { id: "body", icon: Wind, title: "Somatic Grounding & Vitality", text: "Reconnect with your body's intelligence and restore physical energy." },
  { id: "presence", icon: Sun, title: "Mindful Presence & Focus", text: "Quiet the mental noise and align with the present moment." },
  { id: "purpose", icon: Compass, title: "Purposeful Self-Discovery", text: "Clarify your vision and align daily life with what feels true." },
  { id: "relational", icon: HeartHandshake, title: "Relational Harmony", text: "Foster self-compassion, healthy boundaries, and authentic expression." },
];

const ARRIVAL_TILES = [
  { id: "burnout", icon: BatteryLow, tone: "#144F75", title: "Burnout & Exhaustion", text: "Running on empty. You need real rest — not just a weekend off.", to: "/wellness?needs=rest,body" },
  { id: "stress", icon: CloudRain, tone: "#5B8A72", title: "Stress & Anxiety", text: "A racing mind and a tight chest. Find calm you can return to.", to: "/wellness?needs=presence,emotional" },
  { id: "relationships", icon: HeartHandshake, tone: "#D98E73", title: "Relationship & Family Strain", text: "Distance where there used to be closeness — at home or in love.", to: "/wellness?needs=relational" },
  { id: "team", icon: Briefcase, tone: "#9E5C7F", title: "Team & Workplace Disconnection", text: "Your people feel scattered. Bring them back together, on purpose.", to: "#corporate" },
  { id: "recovery", icon: Sprout, tone: "#5B8A72", title: "Recovery & Healing", text: "Rebuilding after addiction, illness, or a hard season of life.", to: "/wellness?needs=purpose,body" },
  { id: "grief", icon: Flower2, tone: "#144F75", title: "Grief & Loss", text: "Carrying something heavy. You don't have to carry it alone.", to: "/wellness?needs=emotional" },
  { id: "spiritual", icon: Sparkles, tone: "#B5794E", title: "Spiritual Exploration", text: "A quiet pull toward something deeper. Begin the exploration.", to: "/gauge/app" },
];

const CORPORATE_OFFERS = [
  { icon: Briefcase, title: "Corporate Wellness Days", text: "On-site or at our centers — massage, breathwork, sound journeys, and restorative sessions that give your team a true reset." },
  { icon: Users, title: "Team Bonding Experiences", text: "Facilitated circles, guided outdoor experiences, and shared practices that rebuild trust and connection between colleagues." },
  { icon: Tent, title: "Group Retreats", text: "Half-day to multi-day retreats for leadership teams, departments, or whole organizations — designed around your goals." },
];

const MEMBER_BENEFITS = [
  { icon: Users, title: "Facilitated Circles & Spirals", text: "Intimate, supportive spaces — men's, women's, couples, and community circles guided by experienced facilitators." },
  { icon: Sparkle, title: "Live Experiential Gatherings", text: "Workshops, sound journeys, breathwork, and retreats focused on practical wisdom and shared experience." },
  { icon: Globe2, title: "A Grounded Local Community", text: "Real people, real places — centers and gatherings where connection happens in person, not just on a screen." },
];

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const Stars = ({ n }) => (
  <span className="inline-flex gap-0.5" aria-label={`${n} stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={15} className={i <= Math.round(n || 0) ? "fill-[#D98E73] text-[#D98E73]" : "text-[#E2DACD]"} />
    ))}
  </span>
);

const StepBadge = ({ n, tone }) => (
  <span className="relative z-10 grid place-items-center h-12 w-12 rounded-full bg-[#F9F7F3] border-2 font-serif text-lg shrink-0" style={{ borderColor: tone, color: tone }}>{n}</span>
);

function fmtTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${ampm}`;
}

export default function Home() {
  const { data: events = [] } = useQuery({ queryKey: ["events"], queryFn: () => api.get("/events").then((r) => r.data) });
  const { data: reviewData } = useQuery({ queryKey: ["google-reviews"], queryFn: () => api.get("/google-reviews").then((r) => r.data) });
  const { data: homepage } = useQuery({ queryKey: ["homepage-content"], queryFn: () => api.get("/site/homepage").then((r) => r.data) });

  const [selectedNeeds, setSelectedNeeds] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [corporateOpen, setCorporateOpen] = useState(false);
  const now = new Date();
  const [month, setMonth] = useState({ y: now.getFullYear(), m: now.getMonth() });

  const heroImg = homepage?.images?.hero || HERO_IMG;
  const toggleNeed = (id) => setSelectedNeeds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const shiftMonth = (d) => setMonth(({ y, m }) => { const nm = m + d; return { y: y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 }; });

  const monthEvents = useMemo(() => {
    const key = `${month.y}-${String(month.m + 1).padStart(2, "0")}`;
    return expandOccurrences(events).filter((e) => (e.date || "").startsWith(key))
      .sort((a, b) => (a.date + (a.start_time || "")).localeCompare(b.date + (b.start_time || "")));
  }, [events, month]);

  const reviews = useMemo(() => {
    return (reviewData?.reviews || []).filter((r) => r.text && (r.rating || 0) >= 4).sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 3);
  }, [reviewData]);
  const reviewsUrl = reviewData?.places?.[0]?.place_id
    ? `https://www.google.com/maps/place/?q=place_id:${reviewData.places[0].place_id}`
    : "https://www.google.com/maps";

  return (
    <div data-testid="home-page" className="bg-[#F9F7F3]">
      <Seo
        title="Holistic Wellness, Recovery & Retreats"
        description="Holistic recovery, energy work, bodywork, coaching, retreats, and community in Newport and Verona, KY. One living ecosystem of well-being — within ourselves, with each other, and with our world."
        image={HERO_IMG}
        path="/"
        type="website"
      />
      <section className="relative min-h-[92vh] flex items-center" data-testid="home-hero">
        <img src={heroImg} alt="Friends walking together through a sunlit forest" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F3A56]/88 via-[#144F75]/55 to-[#0F3A56]/15" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full py-24">
          <div className="max-w-2xl eyw-reveal">
            <p className="text-xs tracking-[0.22em] uppercase font-semibold mb-4 text-[#D98E73]">A Living Ecosystem of Well-Being</p>
            <h1 data-testid="hero-headline" className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
              Enhancing Your World Enhances Our World
            </h1>
            <p data-testid="hero-subheadline" className="text-white/85 text-lg md:text-xl mt-6 max-w-xl leading-relaxed">
              Co-creating holistic well-being within ourselves, with each other, and through how we live on this planet.
            </p>
            <div className="flex flex-wrap gap-3 mt-9">
              <Link to="/gauge/app" data-testid="hero-primary-cta" className="eyw-btn !bg-[#D98E73] hover:!bg-[#c67a60]">
                Begin Your Exploration <ArrowRight size={17} />
              </Link>
              <Link to="/start" data-testid="hero-secondary-cta" className="eyw-btn-ghost !border-white !text-white hover:!bg-white hover:!text-[#144F75]">
                Discover Enhancing Your World
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHAT BRINGS YOU HERE? ============ */}
      <section className="bg-white border-b border-[#144F75]/10 py-16 md:py-20" data-testid="home-arrivals">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-10">
            <p className="text-xs tracking-[0.22em] uppercase font-semibold mb-3 text-[#D98E73]">Start Where You Are</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A242B] tracking-tight leading-tight">What brings you here?</h2>
            <p className="text-[#4A5D6B] text-lg mt-4 leading-relaxed">Most people arrive carrying something real. Pick what feels closest, and we'll show you offerings and practitioners who can help.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="arrival-tiles">
            {ARRIVAL_TILES.map((t, i) => {
              const Icon = t.icon;
              const inner = (
                <>
                  <span className="grid place-items-center h-11 w-11 rounded-xl mb-4 transition-colors" style={{ background: `${t.tone}14`, color: t.tone }}><Icon size={20} /></span>
                  <p className="font-serif text-lg text-[#1A242B] leading-snug">{t.title}</p>
                  <p className="text-[#4A5D6B] text-sm mt-2 leading-relaxed">{t.text}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium mt-4 group-hover:gap-2 transition-all" style={{ color: t.tone }}>Explore this path <ArrowRight size={14} /></span>
                </>
              );
              const cls = "group text-left rounded-2xl border-t-4 border border-[#144F75]/10 bg-[#F9F7F3] p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md flex flex-col eyw-reveal";
              return t.to.startsWith("#") ? (
                <button key={t.id} type="button" data-testid={`arrival-tile-${t.id}`} className={cls} style={{ borderTopColor: t.tone, animationDelay: `${i * 40}ms` }}
                  onClick={() => document.getElementById(t.to.slice(1))?.scrollIntoView({ behavior: "smooth" })}>{inner}</button>
              ) : (
                <Link key={t.id} to={t.to} data-testid={`arrival-tile-${t.id}`} className={cls} style={{ borderTopColor: t.tone, animationDelay: `${i * 40}ms` }}>{inner}</Link>
              );
            })}
            <div className="rounded-2xl border border-dashed border-[#144F75]/25 p-6 flex flex-col justify-center items-start bg-[#144F75]/[0.03]" data-testid="arrival-tile-unsure">
              <p className="font-serif text-lg text-[#1A242B] leading-snug">Not sure where to start?</p>
              <p className="text-[#4A5D6B] text-sm mt-2 leading-relaxed">Answer a few gentle questions in our free GAUGE exploration and get suggestions that fit you.</p>
              <Link to="/gauge/app" data-testid="arrival-gauge-cta" className="eyw-btn !py-2.5 text-sm mt-4">Start the Free Exploration <ArrowRight size={14} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ THREE CONNECTED INVITATIONS ============ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 md:pt-28" data-testid="home-invitations">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold mb-3 text-[#8FA89B]">Three Invitations, One Journey</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A242B] tracking-tight leading-tight">Start anywhere. It all connects.</h2>
        </div>

        <div className="relative">
          {/* connecting thread */}
          <div className="hidden md:block absolute left-6 top-6 bottom-6 w-px border-l-2 border-dashed border-[#8FA89B]/40" aria-hidden="true" />

          {/* ---- 01 · WELL-BEING ---- */}
          <div className="md:pl-0 relative pb-20 md:pb-28" data-testid="invitation-wellbeing">
            <div className="flex items-start gap-5">
              <StepBadge n="01" tone="#144F75" />
              <div className="flex-1">
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#144F75] mt-1">Within Ourselves</p>
                <h3 className="font-serif text-3xl md:text-4xl text-[#1A242B] tracking-tight mt-2 leading-tight">Enhance Your Well-Being</h3>
                <p className="text-[#4A5D6B] text-lg mt-3 max-w-2xl leading-relaxed">
                  You don't need to have it all figured out. Start with what you need today — a session,
                  a class, or a conversation with a practitioner who can walk alongside you.
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10 md:ml-[68px]">
              {NEED_CARDS.map((n, i) => {
                const Icon = n.icon;
                const active = selectedNeeds.includes(n.id);
                return (
                  <button key={n.id} type="button" onClick={() => toggleNeed(n.id)} data-testid={`need-card-${n.id}`} aria-pressed={active}
                    className={`group text-left rounded-2xl border p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md eyw-reveal ${active ? "bg-[#F5E6E0] border-[#D98E73]" : "bg-white border-[#144F75]/10"}`}
                    style={{ animationDelay: `${i * 50}ms` }}>
                    <span className={`grid place-items-center h-11 w-11 rounded-xl mb-4 transition-colors ${active ? "bg-[#D98E73] text-white" : "bg-[#144F75]/8 text-[#144F75]"}`}><Icon size={20} /></span>
                    <p className="font-serif text-xl text-[#1A242B] leading-snug">{n.title}</p>
                    <p className="text-[#4A5D6B] text-sm mt-2 leading-relaxed">{n.text}</p>
                  </button>
                );
              })}
            </div>
            <div className="mt-9 md:ml-[68px] flex flex-wrap items-center gap-4">
              <Link to={selectedNeeds.length ? `/wellness?needs=${selectedNeeds.join(",")}` : "/wellness"} data-testid="invitation-wellbeing-cta" className="eyw-btn">
                Explore What's Possible <ArrowRight size={16} />
              </Link>
              {selectedNeeds.length > 0 && (
                <span className="text-sm text-[#4A5D6B]" data-testid="needs-selected-note">
                  Matched to your {selectedNeeds.length} selection{selectedNeeds.length > 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>

          {/* ---- 02 · CONNECTIONS ---- */}
          <div className="relative pb-20 md:pb-28" data-testid="invitation-connections">
            <div className="flex items-start gap-5">
              <StepBadge n="02" tone="#D98E73" />
              <div className="flex-1">
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D98E73] mt-1">With Each Other</p>
                <h3 className="font-serif text-3xl md:text-4xl text-[#1A242B] tracking-tight mt-2 leading-tight">Enhance Your Connections</h3>
                <p className="text-[#4A5D6B] text-lg mt-3 max-w-2xl leading-relaxed">
                  We grow in relationship. Membership opens the door to circles, gatherings and member
                  pricing — alongside people asking the same questions you are.
                </p>
              </div>
            </div>
            <div className="grid lg:grid-cols-[1fr_1.05fr] gap-8 mt-10 md:ml-[68px] items-center">
              <div className="grid gap-4">
                {MEMBER_BENEFITS.map((b) => {
                  const Icon = b.icon;
                  return (
                    <div key={b.title} className="flex gap-4 bg-white rounded-2xl border border-[#144F75]/10 p-6 shadow-sm">
                      <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#D98E73]/12 text-[#D98E73] shrink-0"><Icon size={20} /></span>
                      <div>
                        <p className="font-serif text-xl text-[#1A242B]">{b.title}</p>
                        <p className="text-[#4A5D6B] text-sm mt-1.5 leading-relaxed">{b.text}</p>
                      </div>
                    </div>
                  );
                })}
                <div className="mt-3">
                  <Link to="/join" data-testid="invitation-connections-cta" className="eyw-btn !bg-[#D98E73] hover:!bg-[#c67a60]">Become a Member <ArrowRight size={16} /></Link>
                </div>
              </div>
              <img src={CONNECT_IMG} alt="A circle of people gathered together outdoors" className="rounded-3xl w-full h-[220px] sm:h-[300px] md:h-[380px] object-cover shadow-lg" loading="lazy" />
            </div>
          </div>

          {/* ---- 03 · OUR WORLD ---- */}
          <div className="relative pb-20 md:pb-24" data-testid="invitation-world">
            <div className="flex items-start gap-5">
              <StepBadge n="03" tone="#5B8A72" />
              <div className="flex-1">
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#5B8A72] mt-1">With Each Other and With Our World</p>
                <h3 className="font-serif text-3xl md:text-4xl text-[#1A242B] tracking-tight mt-2 leading-tight">Enhance Our World</h3>
              </div>
            </div>
            <div className="grid lg:grid-cols-[1.05fr_1fr] gap-8 mt-10 md:ml-[68px] items-center">
              <img src={WORLD_IMG} alt="People exploring nature together in warm light" className="rounded-3xl w-full h-[220px] sm:h-[300px] md:h-[380px] object-cover shadow-lg order-2 lg:order-1" loading="lazy" />
              <div className="order-1 lg:order-2">
                <p className="font-serif text-2xl text-[#144F75] leading-snug">A Talent Agency for Humanity.</p>
                <p className="text-[#4A5D6B] text-lg mt-4 leading-relaxed">
                  When people share their gifts, whole communities benefit. Our Talent Agency for Humanity
                  connects practitioners, teachers and makers with the people, centers and earth-care
                  projects that need what they offer.
                </p>
                <p className="text-[#4A5D6B] mt-3 leading-relaxed">
                  Practitioners, teachers, creators, stewards — your world needs what you carry.
                </p>
                <div className="flex flex-wrap gap-3 mt-7">
                  <Link to="/partner" data-testid="invitation-world-cta" className="eyw-btn">Become an Enhancer <ArrowRight size={16} /></Link>
                  <Link to="/stewardship" data-testid="invitation-world-stewardship-link" className="eyw-btn-outline">Explore Stewardship</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOR TEAMS & ORGANIZATIONS ============ */}
      <section id="corporate" className="py-20 md:py-28 bg-[#0F3A56]" data-testid="home-corporate">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 items-start">
            <div className="lg:sticky lg:top-28">
              <p className="text-xs tracking-[0.22em] uppercase font-semibold mb-3 text-[#D98E73] flex items-center gap-2"><Building2 size={14} /> For Teams & Organizations</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight">Bring well-being to your people.</h2>
              <p className="text-white/80 text-lg mt-5 leading-relaxed">
                Burnout and disconnection affect whole teams, not just individuals. We design group
                experiences — at our centers, outdoors or at your workplace — that restore energy,
                rebuild trust and give your people something they'll still talk about on Monday.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/events" data-testid="corporate-browse-cta" className="eyw-btn !bg-[#D98E73] hover:!bg-[#c67a60]">
                  Browse Group Experiences <ArrowRight size={16} />
                </Link>
                <button type="button" onClick={() => setCorporateOpen(true)} data-testid="corporate-inquiry-cta"
                  className="eyw-btn-ghost !border-white !text-white hover:!bg-white hover:!text-[#144F75]">
                  Submit a Corporate Inquiry
                </button>
              </div>
            </div>
            <div className="grid gap-4">
              {CORPORATE_OFFERS.map((o) => {
                const Icon = o.icon;
                return (
                  <div key={o.title} className="flex gap-5 bg-white/[0.06] backdrop-blur rounded-2xl border border-white/10 p-6" data-testid={`corporate-offer-${o.title.split(" ")[0].toLowerCase()}`}>
                    <span className="grid place-items-center h-12 w-12 rounded-xl bg-[#D98E73]/20 text-[#D98E73] shrink-0"><Icon size={22} /></span>
                    <div>
                      <p className="font-serif text-xl text-white">{o.title}</p>
                      <p className="text-white/70 text-sm mt-1.5 leading-relaxed">{o.text}</p>
                    </div>
                  </div>
                );
              })}
              <p className="text-white/50 text-sm mt-1">From teams of 5 to organizations of 100+ · at our centers, outdoors, or on-site with you.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHAT'S HAPPENING THIS MONTH ============ */}
      <section className="bg-white border-y border-[#144F75]/10 py-20 md:py-28" data-testid="home-events">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div className="max-w-xl">
              <p className="text-xs tracking-[0.22em] uppercase font-semibold mb-3 text-[#8FA89B]">Live Calendar</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A242B] tracking-tight leading-tight">What's Happening This Month</h2>
            </div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => shiftMonth(-1)} data-testid="events-prev-month-btn" aria-label="Previous month"
                className="grid place-items-center h-11 w-11 rounded-full bg-[#F9F7F3] border border-[#144F75]/15 text-[#144F75] hover:bg-[#144F75] hover:text-white transition-colors"><ChevronLeft size={18} /></button>
              <span data-testid="events-month-label" className="font-serif text-xl text-[#1A242B] min-w-[170px] text-center">{MONTHS[month.m]} {month.y}</span>
              <button type="button" onClick={() => shiftMonth(1)} data-testid="events-next-month-btn" aria-label="Next month"
                className="grid place-items-center h-11 w-11 rounded-full bg-[#F9F7F3] border border-[#144F75]/15 text-[#144F75] hover:bg-[#144F75] hover:text-white transition-colors"><ChevronRight size={18} /></button>
            </div>
          </div>

          {monthEvents.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="events-month-grid">
              {monthEvents.map((e) => {
                const d = new Date(`${e.date}T12:00:00`);
                return (
                  <button key={e._occKey} type="button" onClick={() => setSelectedEvent(e)} data-testid={`event-item-card-${e._occKey}`}
                    className="group text-left bg-[#F9F7F3] rounded-2xl border border-[#144F75]/10 p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md flex gap-5">
                    <div className="shrink-0 text-center bg-white rounded-xl border border-[#144F75]/10 px-3.5 py-2.5 h-fit">
                      <div className="text-[10px] uppercase tracking-wider text-[#D98E73] font-semibold">{d.toLocaleString("en-US", { month: "short" })}</div>
                      <div className="font-serif text-2xl text-[#144F75] leading-none mt-0.5">{d.getDate()}</div>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-wide text-[#8FA89B]">{e.category}</p>
                      <h3 className="font-serif text-xl text-[#1A242B] leading-snug mt-1 group-hover:text-[#144F75] transition-colors">{e.title}</h3>
                      <p className="text-[#4A5D6B] text-sm mt-2 flex items-center gap-1.5"><Clock size={13} /> {fmtTime(e.start_time)}{e.end_time ? ` – ${fmtTime(e.end_time)}` : ""}</p>
                      {(e.location_name || e.location) && <p className="text-[#4A5D6B] text-sm mt-1 flex items-center gap-1.5"><MapPin size={13} /> {e.location_name || e.location}</p>}
                      <span className="inline-flex items-center gap-1 text-[#144F75] text-sm font-medium mt-3 group-hover:gap-2 transition-all">View details <ArrowRight size={14} /></span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="rounded-3xl bg-[#F9F7F3] border border-dashed border-[#144F75]/20 p-12 text-center" data-testid="events-empty-state">
              <CalendarDays className="mx-auto text-[#8FA89B] mb-4" size={34} />
              <p className="font-serif text-2xl text-[#1A242B]">Nothing scheduled in {MONTHS[month.m]} yet.</p>
              <p className="text-[#4A5D6B] mt-2">New gatherings are added often — try another month or see the full calendar.</p>
            </div>
          )}
          <div className="mt-10">
            <Link to="/events" data-testid="events-full-calendar-link" className="eyw-btn-outline">See the Full Calendar <ArrowRight size={15} /></Link>
          </div>
        </div>
      </section>

      {/* ============ WHAT PEOPLE ARE SAYING ============ */}
      {reviews.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-28" data-testid="home-reviews">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div className="max-w-xl">
              <p className="text-xs tracking-[0.22em] uppercase font-semibold mb-3 text-[#D98E73]">From Our Community</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A242B] tracking-tight leading-tight">What People Are Saying</h2>
              {reviewData?.average_rating && (
                <p className="text-[#4A5D6B] mt-3 flex items-center gap-2" data-testid="reviews-average-rating">
                  <Stars n={reviewData.average_rating} /> {reviewData.average_rating} · {reviewData.total_rating_count} reviews on Google
                </p>
              )}
            </div>
            <a href={reviewsUrl} target="_blank" rel="noopener noreferrer" data-testid="view-all-reviews-link" className="eyw-btn-ghost !py-2.5 text-sm">
              Read All Google Reviews <ArrowUpRight size={15} />
            </a>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {reviews.map((r, i) => (
              <article key={i} data-testid={`review-card-${i}`} className="bg-white rounded-2xl border border-[#144F75]/10 p-7 shadow-sm flex flex-col">
                <Stars n={r.rating} />
                <p className="font-serif text-lg text-[#144F75] leading-relaxed mt-4 flex-1 line-clamp-6">"{r.text}"</p>
                <div className="flex items-center gap-3 mt-6">
                  {r.author_photo
                    ? <img src={r.author_photo} alt={r.author_name} className="h-10 w-10 rounded-full object-cover" referrerPolicy="no-referrer" loading="lazy" />
                    : <span className="h-10 w-10 rounded-full bg-[#144F75] text-white grid place-items-center text-sm">{(r.author_name || "G").charAt(0)}</span>}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#1A242B] truncate">{r.author_name}</p>
                    <p className="text-xs text-[#8FA89B]">{r.relative_time}{r.place_label ? ` · ${r.place_label}` : ""}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* ============ CLOSING STATEMENT ============ */}
      <section className="py-24 md:py-32" style={{ background: "linear-gradient(135deg, #144F75 0%, #23606f 55%, #5B8A72 100%)" }} data-testid="home-closing">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Compass className="mx-auto text-[#D98E73] mb-7" size={36} />
          <h2 data-testid="closing-statement" className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F9F7F3] tracking-tight leading-[1.15]">
            Within Ourselves. With Each Other. With Our World.
          </h2>
          <p className="font-serif text-xl md:text-2xl text-white/85 mt-6 italic">
            Because Enhancing Your World Enhances Our World.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-10">
            <Link to="/gauge/app" data-testid="closing-explore-btn" className="eyw-btn !bg-[#D98E73] hover:!bg-[#c67a60]">Take the Free GAUGE Exploration</Link>
            <Link to="/join" data-testid="closing-member-btn" className="eyw-btn-ghost !border-white !text-white hover:!bg-white hover:!text-[#144F75]">Become a Member</Link>
          </div>
        </div>
      </section>

      {selectedEvent && <EventModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />}
      {corporateOpen && <CorporateInquiryModal onClose={() => setCorporateOpen(false)} />}
    </div>
  );
}
